import * as stylex from '@stylexjs/stylex'

import { Input } from './FormControls'
import { localDateInputValue } from '../lib/ledger'

const styles = stylex.create({
  root: {
    display: 'grid',
    gap: '16px',
    '@media (min-width: 640px)': {
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    },
  },
  field: { display: 'flex', minWidth: 0, flexDirection: 'column', gap: '8px' },
  label: { fontSize: '13px', fontWeight: 600, color: '#3d463f' },
  quickRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  quickButton: {
    minHeight: '44px',
    borderRadius: '9999px',
    borderWidth: 1,
    borderColor: '#dbe2d9',
    backgroundColor: '#fff',
    paddingInline: '11px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#607068',
    ':hover': { backgroundColor: '#f2f8f1', color: '#3e7d4d' },
    ':focus-visible': { outline: '2px solid #5b9a6c', outlineOffset: '2px' },
  },
  quickSelected: { borderColor: '#b9d9bd', backgroundColor: '#edf7eb', color: '#397a4b' },
  addTime: {
    display: 'flex',
    minHeight: '44px',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '0.75rem',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#87978a',
    backgroundColor: '#fff',
    paddingInline: '12px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#4d7257',
    ':hover': { backgroundColor: '#f3f8f2', borderColor: '#a9c6ad' },
    ':focus-visible': { outline: '2px solid #397a4b', outlineOffset: '2px' },
  },
  noTime: { fontSize: '11px', fontWeight: 500, color: '#59665d' },
  timeRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  timeInput: { flex: '1 1 0%', minWidth: 0 },
  remove: {
    minHeight: '44px',
    flexShrink: 0,
    borderRadius: '0.75rem',
    paddingInline: '11px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#7a817b',
    ':hover': { backgroundColor: '#f0f3ef', color: '#4e5d52' },
  },
  hint: { fontSize: '11px', lineHeight: '1.35', color: '#59665d' },
})

const TIME_PRESETS = [
  { label: 'Morning', value: '08:00' },
  { label: 'Afternoon', value: '13:00' },
  { label: 'Evening', value: '18:00' },
  { label: 'Night', value: '21:00' },
] as const

type Props = {
  date: string
  time: string
  onDateChange: (value: string) => void
  onTimeChange: (value: string) => void
  dateLabel?: string
  timeLabel?: string
  disabled?: boolean
  showQuickDates?: boolean
}

export default function TransactionDateTimeField({
  date,
  time,
  onDateChange,
  onTimeChange,
  dateLabel = 'Date',
  timeLabel = 'Time',
  disabled = false,
  showQuickDates = true,
}: Props) {
  const today = localDateInputValue(new Date())
  const yesterdayDate = new Date()
  yesterdayDate.setDate(yesterdayDate.getDate() - 1)
  const yesterday = localDateInputValue(yesterdayDate)

  return (
    <div {...stylex.props(styles.root)}>
      <div {...stylex.props(styles.field)}>
        <span {...stylex.props(styles.label)}>{dateLabel}</span>
        {showQuickDates && (
          <div {...stylex.props(styles.quickRow)}>
            <button
              aria-pressed={date === today}
              {...stylex.props(styles.quickButton, date === today && styles.quickSelected)}
              disabled={disabled}
              onClick={() => onDateChange(today)}
              type="button"
            >
              Today
            </button>
            <button
              aria-pressed={date === yesterday}
              {...stylex.props(styles.quickButton, date === yesterday && styles.quickSelected)}
              disabled={disabled}
              onClick={() => onDateChange(yesterday)}
              type="button"
            >
              Yesterday
            </button>
          </div>
        )}
        <Input
          aria-label={dateLabel}
          disabled={disabled}
          onChange={(event) => onDateChange(event.target.value)}
          required
          type="date"
          value={date}
        />
      </div>

      <div {...stylex.props(styles.field)}>
        <span {...stylex.props(styles.label)}>{timeLabel}</span>
        <div {...stylex.props(styles.quickRow)}>
          {TIME_PRESETS.map((preset) => (
            <button
              aria-pressed={time === preset.value}
              {...stylex.props(styles.quickButton, time === preset.value && styles.quickSelected)}
              disabled={disabled}
              key={preset.value}
              onClick={() => onTimeChange(preset.value)}
              type="button"
            >
              {preset.label}
            </button>
          ))}
        </div>
        {time ? (
          <div {...stylex.props(styles.timeRow)}>
            <Input
              aria-label={timeLabel}
              {...stylex.props(styles.timeInput)}
              disabled={disabled}
              onChange={(event) => onTimeChange(event.target.value)}
              type="time"
              value={time}
            />
            <button {...stylex.props(styles.remove)} disabled={disabled} onClick={() => onTimeChange('')} type="button">
              Remove
            </button>
          </div>
        ) : (
          <button {...stylex.props(styles.addTime)} disabled={disabled} onClick={() => onTimeChange('18:00')} type="button">
            <span>+ Add exact time</span>
            <span {...stylex.props(styles.noTime)}>None by default</span>
          </button>
        )}
        <p {...stylex.props(styles.hint)}>Time is optional. Presets can still be adjusted to an exact time.</p>
      </div>
    </div>
  )
}
